import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DollarSign, 
  ArrowUpRight, 
  ArrowDownRight,
  Building2,
  CheckCircle,
  Clock,
  Plus,
  Search,
  FileText,
  CreditCard,
  Landmark
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const NASPayments = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialogs
  const [showSupplierPaymentDialog, setShowSupplierPaymentDialog] = useState(false);
  const [showBankDepositDialog, setShowBankDepositDialog] = useState(false);
  
  // Forms
  const [supplierPaymentForm, setSupplierPaymentForm] = useState({
    supplierName: '',
    amount: '',
    paymentMethod: 'cash',
    checkNumber: '',
    checkDate: '',
    bankName: '',
    referenceNumber: '',
    notes: ''
  });
  
  const [bankDepositForm, setBankDepositForm] = useState({
    paymentIds: [],
    bankName: '',
    accountNumber: '',
    depositAmount: '',
    depositDate: new Date().toISOString().split('T')[0],
    depositSlipNumber: '',
    notes: ''
  });
  
  const [selectedPayments, setSelectedPayments] = useState([]);

  // Check access
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  const isAdmin = user?.role === 'admin';
  const canManagePayments = isAdmin || ['alma', 'nicole', 'kristine'].some(n => username.includes(n) || fullName.includes(n));
  const isAlma = isAdmin || username.includes('alma') || fullName.includes('alma');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/nas/payments/dashboard`);
      setDashboard(res.data);
    } catch (error) {
      console.error('Error loading payment dashboard:', error);
      toast.error('Failed to load payment dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', { 
      year: 'numeric', month: 'short', day: 'numeric' 
    });
  };

  const handleCreateSupplierPayment = async (e) => {
    e.preventDefault();
    if (!supplierPaymentForm.supplierName || !supplierPaymentForm.amount) {
      toast.error('Please fill in supplier name and amount');
      return;
    }

    try {
      await axios.post(`${API}/api/nas/payments/supplier`, {
        ...supplierPaymentForm,
        amount: parseFloat(supplierPaymentForm.amount)
      });
      toast.success('Supplier payment created');
      setShowSupplierPaymentDialog(false);
      setSupplierPaymentForm({
        supplierName: '', amount: '', paymentMethod: 'cash',
        checkNumber: '', checkDate: '', bankName: '', referenceNumber: '', notes: ''
      });
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    }
  };

  const handleConfirmPayment = async (paymentId) => {
    try {
      await axios.put(`${API}/api/nas/payments/customer/${paymentId}/confirm`);
      toast.success('Payment confirmed');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    }
  };

  const handleCompleteSupplierPayment = async (paymentId) => {
    try {
      await axios.put(`${API}/api/nas/payments/supplier/${paymentId}/complete`);
      toast.success('Payment marked as completed');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete payment');
    }
  };

  const handleCreateBankDeposit = async (e) => {
    e.preventDefault();
    if (selectedPayments.length === 0) {
      toast.error('Please select payments to deposit');
      return;
    }
    if (!bankDepositForm.bankName || !bankDepositForm.depositAmount) {
      toast.error('Please fill in bank name and deposit amount');
      return;
    }

    try {
      await axios.post(`${API}/api/nas/payments/bank-deposit`, {
        ...bankDepositForm,
        paymentIds: selectedPayments,
        depositAmount: parseFloat(bankDepositForm.depositAmount)
      });
      toast.success('Bank deposit recorded');
      setShowBankDepositDialog(false);
      setSelectedPayments([]);
      setBankDepositForm({
        paymentIds: [], bankName: '', accountNumber: '',
        depositAmount: '', depositDate: new Date().toISOString().split('T')[0],
        depositSlipNumber: '', notes: ''
      });
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record deposit');
    }
  };

  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments(prev => 
      prev.includes(paymentId) 
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payment Dashboard</h1>
          <p className="text-slate-600">Track incoming and outgoing payments</p>
        </div>
        <div className="flex gap-2">
          {canManagePayments && (
            <Button onClick={() => setShowSupplierPaymentDialog(true)} className="bg-red-600 hover:bg-red-700">
              <ArrowUpRight size={18} className="mr-2" />
              Supplier Payment
            </Button>
          )}
          {isAlma && selectedPayments.length > 0 && (
            <Button onClick={() => setShowBankDepositDialog(true)} className="bg-blue-600 hover:bg-blue-700">
              <Landmark size={18} className="mr-2" />
              Bank Deposit ({selectedPayments.length})
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
                <ArrowDownRight className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-green-700">Incoming (Confirmed)</p>
                <p className="text-2xl font-bold text-green-800">
                  {formatCurrency(dashboard?.summary?.incoming?.confirmed || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                <Clock className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-amber-700">Pending Confirmation</p>
                <p className="text-2xl font-bold text-amber-800">
                  {formatCurrency(dashboard?.summary?.incoming?.pending || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-500 flex items-center justify-center">
                <ArrowUpRight className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-red-700">Outgoing (Completed)</p>
                <p className="text-2xl font-bold text-red-800">
                  {formatCurrency(dashboard?.summary?.outgoing?.completed || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
                <Landmark className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-blue-700">Bank Deposits</p>
                <p className="text-2xl font-bold text-blue-800">
                  {formatCurrency(dashboard?.summary?.bankDeposits?.totalDeposited || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="incoming">Incoming Payments</TabsTrigger>
          <TabsTrigger value="outgoing">Supplier Payments</TabsTrigger>
          <TabsTrigger value="deposits">Bank Deposits</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Incoming */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowDownRight size={20} className="text-green-600" />
                  Recent Customer Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard?.customerPayments?.slice(0, 5).map(payment => (
                    <div key={payment.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900">{payment.customerName || 'Unknown'}</p>
                        <p className="text-sm text-slate-500">{formatDate(payment.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          payment.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          payment.status === 'deposited' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {payment.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {(!dashboard?.customerPayments || dashboard.customerPayments.length === 0) && (
                    <p className="text-center text-slate-500 py-4">No recent payments</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Outgoing */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowUpRight size={20} className="text-red-600" />
                  Recent Supplier Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard?.supplierPayments?.slice(0, 5).map(payment => (
                    <div key={payment.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900">{payment.supplierName}</p>
                        <p className="text-sm text-slate-500">{payment.paymentMethod} • {formatDate(payment.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-red-600">{formatCurrency(payment.amount)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {payment.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {(!dashboard?.supplierPayments || dashboard.supplierPayments.length === 0) && (
                    <p className="text-center text-slate-500 py-4">No recent payments</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Incoming Payments Tab */}
        <TabsContent value="incoming">
          <Card>
            <CardHeader>
              <CardTitle>Customer Payments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      {isAlma && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Select</th>}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Method</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Amount</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {dashboard?.customerPayments?.map(payment => (
                      <tr key={payment.id} className="hover:bg-slate-50">
                        {isAlma && (
                          <td className="px-4 py-3">
                            {payment.status === 'confirmed' && (
                              <input
                                type="checkbox"
                                checked={selectedPayments.includes(payment.id)}
                                onChange={() => togglePaymentSelection(payment.id)}
                                className="w-4 h-4 rounded border-slate-300"
                              />
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 font-medium">{payment.customerName || 'Unknown'}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(payment.createdAt)}</td>
                        <td className="px-4 py-3 text-slate-600">{payment.paymentMethod}</td>
                        <td className="px-4 py-3 text-right font-bold text-green-600">{formatCurrency(payment.amount)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            payment.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            payment.status === 'deposited' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isAlma && payment.status === 'pending' && (
                            <Button size="sm" onClick={() => handleConfirmPayment(payment.id)} className="bg-green-600 hover:bg-green-700">
                              <CheckCircle size={14} className="mr-1" />
                              Confirm
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outgoing Payments Tab */}
        <TabsContent value="outgoing">
          <Card>
            <CardHeader>
              <CardTitle>Supplier Payments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Supplier</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Method</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Reference</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Amount</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {dashboard?.supplierPayments?.map(payment => (
                      <tr key={payment.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium">{payment.supplierName}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(payment.createdAt)}</td>
                        <td className="px-4 py-3 text-slate-600">{payment.paymentMethod}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {payment.checkNumber || payment.referenceNumber || '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-red-600">{formatCurrency(payment.amount)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {canManagePayments && payment.status === 'pending' && (
                            <Button size="sm" onClick={() => handleCompleteSupplierPayment(payment.id)} className="bg-green-600 hover:bg-green-700">
                              <CheckCircle size={14} className="mr-1" />
                              Complete
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Deposits Tab */}
        <TabsContent value="deposits">
          <Card>
            <CardHeader>
              <CardTitle>Bank Deposits</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Bank</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Slip #</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Deposited By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {dashboard?.bankDeposits?.map(deposit => (
                      <tr key={deposit.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">{formatDate(deposit.depositDate)}</td>
                        <td className="px-4 py-3 font-medium">{deposit.bankName}</td>
                        <td className="px-4 py-3 text-slate-600">{deposit.depositSlipNumber || '-'}</td>
                        <td className="px-4 py-3 text-right font-bold text-blue-600">{formatCurrency(deposit.depositAmount)}</td>
                        <td className="px-4 py-3 text-slate-600">{deposit.createdBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Supplier Payment Dialog */}
      <Dialog open={showSupplierPaymentDialog} onOpenChange={setShowSupplierPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Supplier Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSupplierPayment} className="space-y-4">
            <div>
              <Label>Supplier Name *</Label>
              <Input
                value={supplierPaymentForm.supplierName}
                onChange={e => setSupplierPaymentForm({...supplierPaymentForm, supplierName: e.target.value})}
                placeholder="Enter supplier name"
                required
              />
            </div>
            <div>
              <Label>Amount *</Label>
              <Input
                type="number"
                value={supplierPaymentForm.amount}
                onChange={e => setSupplierPaymentForm({...supplierPaymentForm, amount: e.target.value})}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select
                value={supplierPaymentForm.paymentMethod}
                onValueChange={v => setSupplierPaymentForm({...supplierPaymentForm, paymentMethod: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="gcash">GCash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {supplierPaymentForm.paymentMethod === 'check' && (
              <>
                <div>
                  <Label>Check Number</Label>
                  <Input
                    value={supplierPaymentForm.checkNumber}
                    onChange={e => setSupplierPaymentForm({...supplierPaymentForm, checkNumber: e.target.value})}
                    placeholder="Check number"
                  />
                </div>
                <div>
                  <Label>Check Date</Label>
                  <Input
                    type="date"
                    value={supplierPaymentForm.checkDate}
                    onChange={e => setSupplierPaymentForm({...supplierPaymentForm, checkDate: e.target.value})}
                  />
                </div>
              </>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={supplierPaymentForm.notes}
                onChange={e => setSupplierPaymentForm({...supplierPaymentForm, notes: e.target.value})}
                placeholder="Additional notes"
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSupplierPaymentDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700">
                Create Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bank Deposit Dialog */}
      <Dialog open={showBankDepositDialog} onOpenChange={setShowBankDepositDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Bank Deposit</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateBankDeposit} className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>{selectedPayments.length}</strong> payment(s) selected for deposit
              </p>
            </div>
            <div>
              <Label>Bank Name *</Label>
              <Input
                value={bankDepositForm.bankName}
                onChange={e => setBankDepositForm({...bankDepositForm, bankName: e.target.value})}
                placeholder="e.g., BDO, BPI, Metrobank"
                required
              />
            </div>
            <div>
              <Label>Account Number</Label>
              <Input
                value={bankDepositForm.accountNumber}
                onChange={e => setBankDepositForm({...bankDepositForm, accountNumber: e.target.value})}
                placeholder="Account number"
              />
            </div>
            <div>
              <Label>Deposit Amount *</Label>
              <Input
                type="number"
                value={bankDepositForm.depositAmount}
                onChange={e => setBankDepositForm({...bankDepositForm, depositAmount: e.target.value})}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <Label>Deposit Date *</Label>
              <Input
                type="date"
                value={bankDepositForm.depositDate}
                onChange={e => setBankDepositForm({...bankDepositForm, depositDate: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Deposit Slip Number</Label>
              <Input
                value={bankDepositForm.depositSlipNumber}
                onChange={e => setBankDepositForm({...bankDepositForm, depositSlipNumber: e.target.value})}
                placeholder="Slip number"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={bankDepositForm.notes}
                onChange={e => setBankDepositForm({...bankDepositForm, notes: e.target.value})}
                placeholder="Additional notes"
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBankDepositDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Record Deposit
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASPayments;
